// どんなときでもhooksでどうにか処理を差し込むやつ。
// Promise返せる感じ

type HookFunction = (currentValue, callerContext, args) => any

type callHooks = (hookName: string, currentValue: any, callerContext: any, additionalArgs?: any) => Promise<any>
interface stringValuedObject {
  [keyName: string]: string | stringValuedObject
}

export class AppHook {
  /**
   * 登録されたHookFunctionsを保持しておくためのオブジェクト
   */
  _hooks: { [hookName: string]: { func: HookFunction; name: string }[] }
  /**
   * 外部から、登録可能なHookをここに登録しておくつもりだったけどあんまり使ってない
   */
  hookDefinitions: stringValuedObject

  constructor() {
    this._hooks = {}
    this.hookDefinitions = {}
  }

  /**
   * 真のシングルトンを達成するためのworkaround
   * Ref: https://gist.github.com/dmnsgn/4a6ad76de1b5928f13f68f406c70bb09#gistcomment-3470400
   */
  static get instance(): AppHook {
    if(!globalThis[Symbol.for(`PF_${AppHook.name}`)]) {
      globalThis[Symbol.for(`PF_${AppHook.name}`)] = new this()
    }
    return globalThis[Symbol.for(`PF_${AppHook.name}`)]
  }

  get hooks() {
    return this._hooks
  }

  /**
   * 登録されたHookを実行する
   * @param hookName - 実行するHookの名前: '$embCommerce.cart.updated.after'
   * @param currentValue - 現在の値, 処理によってはこの値が変更する対象となる (hook実行後の返り値)
   * @param callerContext
   * @param additionalArgs
   */
  // async callHooks(hookName: string, currentValue: any = null, callerContext: any = null, additionalArgs: any = {}): Promise<any> {
  async callHooks(hookName: string, currentValue: any = null, callerContext: any = null, additionalArgs: any = {}): Promise<any> {
    if (!this.hasHook(hookName)) {
      // console.log(`[$appHook] No registered function(s) for hookName: '${hookName}'`)
      return currentValue
    }
    // console.log(`[$appHook] calling hooks, hookName: '${hookName}', ${this._hooks[hookName].length} functions`)
    let result = currentValue
    for (let i = 0; i < this._hooks[hookName].length; i++) {
      const func = this._hooks[hookName][i].func
      result = func(result, callerContext, additionalArgs)
      if (result && typeof result.then === 'function') {
        result = await result
      }
      // console.log(
      //   `[$appHook] Executed hookName: '${hookName}', funcName: '${this._hooks[hookName][i].name}', result:`,
      //   result
      // )
    }
    return result
  }

  /**
   * PromiseではないHookでCallする
   * Promiseなhookを挟むと失敗する。
   * @param hookName
   * @param currentValue
   * @param callerContext
   * @param additionalArgs
   */
  callHooksNonAsync<T = any>(hookName: string, currentValue: any = null, callerContext: any = null, additionalArgs: any = {}): T {
    if (!this._hooks[hookName] || this._hooks[hookName].length === 0) {
      // console.log(`[$appHook] No registered function(s) for hookName: '${hookName}'`)
      return currentValue
    }
    let result = currentValue
    for (let i = 0; i < this._hooks[hookName].length; i++) {
      const func = this._hooks[hookName][i].func
      result = func(result, callerContext, additionalArgs)
      console.log(
        `[$appHook] Executed hookName: '${hookName}', funcName: '${this._hooks[hookName][i].name}', result:`,
        result
      )
    }
    return result
  }

  hasHook(hookName: string): boolean {
    return !!this._hooks[hookName] && this._hooks[hookName].length > 0
  }

  addHookDefinitions (hookBaseName: string, hookDefinitions: stringValuedObject) {
    this.hookDefinitions[hookBaseName] = hookDefinitions
  }
  /**
   * hookを登録しておく, このときに、priority 順で処理順番を制御できる。
   * functionName で重複制御, 再登録されることはない
   * @param hookName
   * @param functionName
   * @param func
   * @param priority
   */
  registerHook(hookName: string, functionName: string = 'd', func: HookFunction, priority = 0): void {
    if (!this._hooks[hookName]) {
      this._hooks[hookName] = []
    }
    if (this._hooks[hookName].find(h => h.name === functionName)) {
      console.log(`Overriding func: "${functionName}" of hook "${hookName}"`)
      this.removeHook(hookName, functionName)
    }
    this._hooks[hookName].splice(priority, 0, {func, name: functionName})
  }

  removeHook(hookName: string, functionName: string): void {
    this._hooks[hookName] = this._hooks[hookName].filter((f) => f.name !== functionName)
  }

  /**
   * 関数を実行するときに、 before hook, after hook をそれぞれCallする
   * @param mainFunc - メイン処理関数
   * @param hookBaseName
   * @param callerContext
   * @param currentValue
   * @param additionalArgs
   */
  async executeWithBeforeAfterHooks({
                                      mainFunc,
                                      hookBaseName,
                                      callerContext,
                                      currentValue = null,
                                      additionalArgs = null
                                    }: {
    mainFunc: (currentValue, callerContext, additionalArgs) => any
    hookBaseName: string
    callerContext: any
    currentValue?: any
    additionalArgs?: any
  }) {
    if (!hookBaseName) {
      throw new Error(`[$appHook] Please set hookBaseName arg correctly`)
    }
    const hooks = genHookSet(hookBaseName)
    try {
      currentValue = await this.callHooks(hooks.BEFORE, currentValue, callerContext, additionalArgs)
      currentValue = await mainFunc(currentValue, callerContext, additionalArgs)
      currentValue = await this.callHooks(hooks.AFTER, currentValue, callerContext, additionalArgs)
      return currentValue
    } catch (e) {
      if (this.hasHook(hooks.ERROR)) {
        return await this.callHooks(hooks.ERROR, e, callerContext, additionalArgs)
      } else {
        throw e
      }
    }
  }

  /**
   * Hook自体が、executableであるものをkickする。
   * hookBaseName.before => hookBaseName.do => hookBaseName.after (hookBaseName.error)
   * の4つのhookがそれぞれcallされる可能性がある
   * @param hookBaseName
   * @param callerContext
   * @param currentValue
   * @param additionalArgs
   */
  async doExecutableHook({ hookBaseName, callerContext = null, currentValue = null, additionalArgs = null }: {
    hookBaseName: string
    callerContext?: any
    currentValue?: any
    additionalArgs?: any
  }) {
    if (hookBaseName.endsWith('.do')) {
      hookBaseName = hookBaseName.replace(/\.do$/, '')
    }
    const execHookName = `${hookBaseName}.do`
    if (!this._hooks[execHookName]) {
      window.alert(`[$appHook] hookBaseName: ${hookBaseName} を実装してください。 $appHook.registerHook('${execHookName}', 'do', func: HookFunction, priority = 0)`)
      return false
    }
    const mainFunc = () => this.callHooks(execHookName, currentValue, callerContext, additionalArgs)
    return this.executeWithBeforeAfterHooks({
      mainFunc, hookBaseName, callerContext, additionalArgs
    })
  }

  /**
   * 登録されたHookを正規表現で探す
   * @param regExp
   */
  async findRegisteredHooksByHookNameRegularExpression(regExp) {
    return Object.keys(this._hooks).reduce((res, r) => {
      if(regExp.test(r)) {
        res[r] = this._hooks[r]
      }
      return res
    }, {})
  }
}

export const $appHook = AppHook.instance
if (typeof process === 'object') {
  // @ts-ignore
  if (!global.window) {
    // @ts-ignore
    global.window = {}
  }
  // @ts-ignore
  global.window.$appHook = $appHook
} else {
  // @ts-ignore
  window.$appHook = $appHook
}

export const genHookSet = (basePath) => {
  return {
    baseName: basePath,
    BEFORE: `${basePath}.before`,
    AFTER: `${basePath}.after`,
    ERROR: `${basePath}.onerror`
  }
}

export const genExecutableHookSet = (basePath) => {
  return {
    DO: `${basePath}.do`,
    ...(genHookSet(basePath))
  }
}
